import type { FrappeDoctypeLookupOption } from "@/api/frappeLookupApi";
import type { CustomerBalance } from "@/type/customerBalance";
import type { DeliveryPartner } from "@/type/deliveryPartner";
import type { TransportTransporterOption } from "@/type/petTransport";
import type { PosTransportJob } from "@/utils/transporterSale";
import type { SalesInvoiceDetail, SalesInvoiceListItem } from "@/type/salesInvoice";

export type PosDiscountType = "Fixed" | "Percentage";
export type PosPaymentStatus = "Paid" | "Due";

/**
 * Refusals the till shows as a block message beside Process Transaction, rather than as a
 * toast that vanishes.
 *
 * The distinction is the point: these are things a cashier can FIX while the customer is
 * still standing there — stock that is not on the shelf, a partner order with no reference
 * number, an invoice that belongs to a boarding stay. Anything not listed here is an error
 * the cashier cannot act on, and it is toasted instead.
 *
 * An unlisted code keeps the backend's own words; it is never remapped.
 */
export const POS_EXPECTED_REFUSAL_CODES = [
  "INSUFFICIENT_STOCK",
  "POS_PARTIAL_PAYMENT_NOT_ALLOWED",
  "PARTNER_INACTIVE",
  "PARTNER_SALE_MUST_BE_DUE",
  "PARTNER_CUSTOMER_MISMATCH",
  "PARTNER_ORDER_REF_REQUIRED",
  /**
   * The invoice already carries prepayments, so it cannot be settled at the counter — the
   * money was taken elsewhere, on a boarding stay. This is the ONE refusal where the
   * backend cannot know the remedy, so the till supplies its own wording. See
   * `alkokh.pos.messages.invoiceCarriesAdvances`.
   */
  "INVOICE_CARRIES_ADVANCES",
  /**
   * A game prize code on the sale was refused inside the sale transaction
   * (docs/backend/pos-game-prizes.md §4). Nothing was saved; the cashier removes the prize
   * or fixes what the message names, and the backend's own words say which.
   */
  "PRIZE_CLAIM_NOT_FOUND",
  "PRIZE_CLAIM_REDEEMED",
  "PRIZE_CLAIM_EXPIRED",
  "PRIZE_CLAIM_WRONG_CUSTOMER",
  "PRIZE_DISCOUNT_MISMATCH",
] as const;

export type PosRefusalCode = typeof POS_EXPECTED_REFUSAL_CODES[number];

export function isPosExpectedRefusal(code: string): code is PosRefusalCode {
  return (POS_EXPECTED_REFUSAL_CODES as readonly string[]).includes(code);
}

export interface PosCatalogItem {
  id: string;
  itemCode: string;
  itemName: string;
  /**
   * `Item.arabic_name`, shown instead of `itemName` while the till is in Arabic.
   *
   * ⚠️ `null` WHEN THE ITEM HAS NONE — not a copy of `itemName`. The display layer falls
   * back to English for itself (`pickLocalizedName`), which is what lets an Arabic session
   * stay readable on a catalogue that is only half translated.
   */
  arabicName: string | null;
  itemGroup: string | null;
  stockUom: string | null;
  description: string | null;
  image: string | null;
  barcode: string | null;
  price: number;
  priceList: string | null;
  priceListCurrency: string | null;
  qtyAvailable: number | null;
  inStock: boolean;
  isStockItem: boolean;
  /** The item's Frappe tags, exact. Optional: older constructors do not set them. */
  tags?: string[];
  /** Carries the till's favourite tag — shown in the quick-pick strip and starred. */
  isFavourite?: boolean;
}

export interface PosCatalogFilterParam {
  search?: string;
  itemGroup?: string | null;
  /** Only items carrying this Frappe tag (substring match on `_user_tags`). */
  tag?: string | null;
  warehouse?: string | null;
  limit_start?: number;
  limit_page_length?: number;
  order_by?: string;
}

export interface PosCatalogResult {
  data: PosCatalogItem[];
  total: number;
}

export interface PosCartItem {
  id: string;
  itemId: string;
  itemCode: string;
  /**
   * The item's English name, kept verbatim from the catalogue.
   *
   * ⚠️ NOT NECESSARILY WHAT IS ON SCREEN. The cart line renders
   * `pickLocalizedName(locale, label, arabicName)`, so an Arabic till shows the Arabic
   * name while this still holds the English one. Keep both: a line built in Arabic and
   * then switched to English must be able to show the English name again, which a single
   * overwritten field could not do.
   */
  label: string;
  /** `Item.arabic_name`, or `null`. Carried so the line can re-resolve on a language switch. */
  arabicName: string | null;
  itemGroup: string | null;
  barcode: string | null;
  image: string | null;
  unitPrice: number;
  priceList: string | null;
  priceListCurrency: string | null;
  qty: number;
  maxQty: number | null;
  inStock: boolean;
  isStockItem: boolean;
  /**
   * Set on the fare line the Transporter sheet writes: this line's money is the
   * transporter's fare, not the clinic's (see `utils/transporterSale.ts`). Edited from the
   * sheet only — never merged by scanning, never quantity-edited in the cart.
   */
  transport?: { transporter: string } | null;
}

export interface PosSalePayload {
  /** Restricted return voucher; atomically consumed, never queued. */
  returnCredit?: { voucher: string; amount: number } | null;
  cashierProfileName: string;
  company?: string | null;
  cashAccount?: string | null;
  customerId: string;
  customerLabel: string | null;
  postingDate: string;
  paymentStatus?: PosPaymentStatus | null;
  paymentMode?: string | null;
  paidAmount: number;
  warehouse?: string | null;
  discountType?: PosDiscountType | null;
  discountValue?: number | null;
  discountAmount?: number | null;
  remarks?: string | null;
  /**
   * Stable per checkout attempt, reused across retries of the SAME sale.
   *
   * ⚠️ Without it a retry after a timeout creates a second invoice and moves the
   * stock twice — the customer is charged again and nothing on screen says so.
   * With it the backend returns the original sale and sets `replayed`.
   */
  idempotencyKey?: string | null;
  /**
   * The number the device ALREADY PRINTED on the customer's receipt.
   *
   * The backend's second replay guard, scoped to the POS profile. It matters because it
   * survives what the idempotency key does not: the key lives only in this device's queue,
   * so a reinstall or a cleared storage loses it, while the receipt is in the customer's
   * hand — and a manager re-keying that sale by hand is recognised as a replay.
   */
  receiptNo?: string | null;
  /** When the sale actually happened. Posts on that date, within the backend's window. */
  offlineCreatedAt?: string | null;
  /** Which till sent it. Audit only; never used for authorisation. */
  deviceId?: string | null;
  /**
   * The delivery partner this order came from (Talabat, Toters …), when the sale
   * was rung up as a partner order.
   *
   * ⚠️ A PARTNER SALE IS ALWAYS `paymentStatus: "Due"`. The app collected the
   * money at its end, so nothing is taken at the counter and the receivable sits
   * against the partner's own customer account until the month is settled. The
   * backend refuses a partner sale that carries a payment.
   *
   * ⚠️ `partnerCommissionRate` AND `partnerCommissionAmount` ARE SNAPSHOTS, NOT
   * INSTRUCTIONS. They record the terms in force at the moment of sale so a later
   * change cannot rewrite history. Neither reduces the invoice — the customer is
   * charged the full total and the commission is booked as an expense when the
   * partner remits.
   *
   * ⚠️ WHICH ONE COUNTS IS THE PARTNER'S CHOICE. Both are sent; the server reads the
   * one the partner's own commission type calls for.
   */
  deliveryPartner?: string | null;
  /** The partner's own order number, for reconciling against their statement. */
  partnerOrderRef?: string | null;
  /** The end customer's name as the app supplied it. Never a Customer record. */
  partnerCustomerName?: string | null;
  partnerCommissionRate?: number | null;
  /**
   * The flat fee for THIS order, when the partner charges one instead of a share.
   * A snapshot, exactly like the rate: it records what was agreed, it does not
   * reduce the invoice.
   */
  partnerCommissionAmount?: number | null;
  /**
   * A kiosk game prize used on this sale. The server redeems it IN THE SAME TRANSACTION as
   * the invoice and re-checks the discount against the prize (docs/backend/pos-game-prizes.md).
   * Omitted entirely when there is none, so an ordinary sale's request is unchanged.
   */
  prizeClaimCode?: string | null;
  /**
   * «Deliver this sale»: the transporter's delivery job, booked by the server IN THE SAME
   * TRANSACTION as the invoice (docs/README.transporter-entity-backend-prompt.md §5). The sale
   * is then Due — he collects it at the door. Omitted entirely otherwise.
   */
  transportJob?: PosTransportJob | null;
  items: PosCartItem[];
}

export interface PosReceiptItem {
  label: string;
  qty: number;
  unitPrice: number;
  amount: number;
}

export interface PosReceiptPayload {
  returnCreditAmount?: number;
  returnCreditVoucher?: string | null;
  invoiceId: string;
  postingDate: string;
  /**
   * Clock time of the sale, `HH:MM[:SS]`, printed as a Time row under the date.
   *
   * ⚠️ Optional and never defaulted. A reprint of an older invoice usually has no time,
   * and `posReceiptPrinter` drops the row rather than stamp the slip with the moment the
   * paper came out — a time the sale never happened at.
   */
  postingTime?: string | null;
  customerLabel: string | null;
  cashierProfileName: string;
  paymentStatus: PosPaymentStatus;
  paymentMode: string | null;
  paidAmount: number;
  changeAmount: number;
  balanceDueAmount: number;
  subtotalAmount: number;
  discountAmount: number;
  discountLabel: string | null;
  totalAmount: number;
  /**
   * Prepayments already held against the settled invoices, and what was therefore actually
   * collectable. Optional: absent on an ordinary counter sale and on any backend that does
   * not send them.
   *
   * ⚠️ `totalAmount` deliberately stays the gross so the printed receipt is unchanged. A
   * template that wants to show the advance must opt in by reading these.
   */
  totalAdvanceAmount?: number | null;
  amountDueAmount?: number | null;
  /**
   * The partner and its order number, printed as receipt meta rows.
   *
   * ⚠️ camelCase, like every other field here — `posReceiptPrinter` reads these
   * keys verbatim and a snake_case copy prints blank with no error at all.
   */
  partnerLabel?: string | null;
  partnerOrderRef?: string | null;
  /**
   * The transporter's share and the clinic's share of this sale, printed under the total
   * when a Transportation line is on it. Filled by the till — the backend receipt does not
   * know the split — so both are absent on every other sale.
   */
  transporterLabel?: string | null;
  transporterShare?: number | null;
  clinicShare?: number | null;
  items: PosReceiptItem[];
}

export interface PosSaleResult {
  invoice: SalesInvoiceDetail;
  paymentEntryId: string | null;
  receipt: PosReceiptPayload;
  /**
   * true when an `idempotencyKey` matched an attempt that had already completed,
   * so the backend returned the original sale rather than charging again. The
   * cashier should be told: nothing new happened and the receipt is a reprint.
   */
  replayed?: boolean;
  /**
   * Always absent on this path — an ordinary sale settles no pre-existing
   * invoice. Declared so the checkout handler can read it off either result
   * without narrowing the union first; see `PosSettleResult.settledInvoiceIds`.
   */
  settledInvoiceIds?: undefined;
}

/**
 * Settling a customer's already-open Draft invoice at the till, with the counter
 * items appended.
 *
 * Backend contract: `docs/backend/pos-settle-open-invoice.md`
 * (`pet_app.api.pos.settle_open_invoice`).
 *
 * ⚠️ THIS CANNOT BE DONE FROM THE CLIENT, AND THE PAYLOAD SHAPE IS WHY.
 * `update_stock` is a header field, so one flag governs the clinic's lines and the
 * counter's together; and `SalesInvoiceDraftItem` cannot carry a per-row
 * `warehouse` / `batch_no`, which a full-document save then drops. The endpoint
 * owns both problems. The client only describes intent.
 */
export interface PosSettlePayload {
  invoiceId: string;
  /**
   * The draft's `modified` as the client last read it. The backend rejects a
   * mismatch instead of overwriting — a vet may be saving the same visit while
   * the cashier stands at checkout, and Frappe's own `check_if_latest` does not
   * catch it because the update path re-reads the document first.
   */
  expectedModified: string | null;
  cashierProfileName: string;
  company?: string | null;
  warehouse?: string | null;
  paymentStatus: PosPaymentStatus;
  paymentMode?: string | null;
  paidAmount: number;
  cashAccount?: string | null;
  discountType?: PosDiscountType | null;
  discountValue?: number | null;
  remarks?: string | null;
  /** The counter cart. An empty list is valid — settle the draft exactly as it is. */
  /** «Deliver this sale» on a settled draft: same as `PosSalePayload.transportJob`. */
  transportJob?: PosTransportJob | null;
  items: PosCartItem[];
}

export interface PosSettleResult {
  invoice: SalesInvoiceDetail;
  paymentEntryId: string | null;
  /**
   * `null` when the endpoint returned nothing printable.
   *
   * ⚠️ Deliberately nullable rather than a best-effort object. A receipt built
   * from a payload that did not match prints blanks and `NaN` onto thermal paper
   * in front of a customer, with no error raised anywhere. The caller rebuilds it
   * from its own state instead, which it can do exactly.
   */
  receipt: PosReceiptPayload | null;
  settledExistingInvoice: boolean;
  appendedItemCount: number;
  /** See `PosSaleResult.replayed` — both POS methods share one envelope. */
  replayed?: boolean;
  /**
   * Every invoice this settlement closed. One entry on the singular path; the
   * whole merged set on the plural one, so the success message can say how many
   * documents the customer just cleared rather than naming only the first.
   */
  settledInvoiceIds?: string[];
}

/** One document in a merged settlement, with the lock the backend checks it against. */
export interface PosSettleTarget {
  invoiceId: string;
  /** See `PosSettlePayload.expectedModified`. Per invoice — the locks are not shared. */
  expectedModified: string | null;
}

/**
 * Settling ALL of a customer's open drafts in one payment.
 *
 * Backend contract: `docs/backend/pos-settle-open-invoice.md`
 * (`pet_app.api.pos.settle_open_invoices`).
 *
 * ⚠️ IDENTICAL TO `PosSettlePayload` EXCEPT FOR `invoices` AND `idempotencyKey`,
 * and both differences are load-bearing. A customer with three clinic drafts pays
 * once, so the settlement is one transaction over three documents — looping the
 * singular endpoint would strand some submitted and paid and others not, with the
 * money already taken. And the singular path shipped with no idempotency key,
 * leaving `expected_modified` as its only replay guard; this one does not repeat
 * that.
 */
export interface PosSettleManyPayload extends Omit<PosSettlePayload, "invoiceId" | "expectedModified"> {
  invoices: PosSettleTarget[];
  idempotencyKey: string;
}

/**
 * One parked sale, as the tab strip sees it.
 *
 * The strip is presentational and must not be handed the whole slot — a snapshot
 * carries a customer, a partner and a cart, none of which a tab button has any
 * business reading.
 */
export interface PosSaleTabView {
  id: string;
  label: string;
  /** Cart lines plus linked drafts: what "this tab has something in it" means. */
  lineCount: number;
}

/** The till's editable sale header — the `form` in `pages/pos/sell.vue`. */
export interface PosSaleSlotForm {
  postingDate: string;
  paymentStatus: PosPaymentStatus;
  paymentMode: string;
  paidAmount: number;
  discountType: PosDiscountType;
  discountValue: number;
  remarks: string;
  /** The partner's own order number. Required while a partner is attached. */
  partnerOrderRef: string;
  /** The end customer's name as the app gave it. Optional, never a Customer record. */
  partnerCustomerName: string;
  /**
   * A game prize applied to this sale, or null. Lives on the form so a sale tab carries
   * its own prize through capture/restore. While set, the discount fields belong to it.
   */
  prize: PosAppliedPrize | null;
}

/**
 * A kiosk game prize applied at the till (docs/backend/pos-game-prizes.md).
 *
 * ⚠️ WHOLE-INVOICE ONLY. Percent and fixed prizes become the sale's own discount; a free
 * item is added as an ordinary line and discounted by exactly one unit's price (never a
 * zero-price line — the till refuses those); loyalty points change nothing at the till.
 */
export interface PosAppliedPrize {
  claimCode: string;
  prizeType: "percent_discount" | "fixed_discount" | "free_item" | "loyalty_points";
  /** Percent, IQD, or points — as the prize defines it. */
  value: number;
  title: string;
  titleAr: string;
  /** Free item only: the item code added to the cart. */
  itemCode: string | null;
  /** The discount fields as they were before the prize, restored when it is removed. */
  previousDiscount: { type: PosDiscountType; value: number };
}

/**
 * One sale tab, frozen. The live sale is the page's own refs; a slot is a COPY that
 * `captureActiveSlot` writes and `activateSlot` restores. Held by `posSales.store`
 * so the tabs outlive the POS page while the cashier visits other screens.
 */
export interface PosSaleSlot {
  id: string;
  /** `null` until renamed; the strip falls back to "Sale N" by position. */
  label: string | null;
  cart: PosCartItem[];
  customerOption: FrappeDoctypeLookupOption | null;
  customerBalance: CustomerBalance | null;
  settleableInvoices: SalesInvoiceListItem[];
  linkedInvoices: SalesInvoiceDetail[];
  activePartner: DeliveryPartner | null;
  /**
   * The clinic's own transporter delivering this sale, and where to. Sent with the sale as
   * `transport_job`. Optional: tabs frozen before the Transporter sheet have none.
   */
  transporter?: TransportTransporterOption | null;
  transportDelivery?: { address: string; phone: string; rideAlongTask: string | null; ridingWith: string | null } | null;
  checkoutIdempotencyKey: string | null;
  checkoutBlockReason: string | null;
  manualCode: string;
  paymentEdited: boolean;
  form: PosSaleSlotForm;
}

/** "Has something in it": cart lines or linked drafts. */
export function posSaleSlotHasWork(slot: Pick<PosSaleSlot, "cart" | "linkedInvoices">) {
  return slot.cart.length > 0 || slot.linkedInvoices.length > 0;
}
